import React from "react";
import { MagnetStraight, Broadcast } from "@phosphor-icons/react";

const CHANNEL_ORDER = [
  "warm outreach",
  "cold outreach",
  "free content",
  "paid ads",
];

function channelRank(c) {
  const i = CHANNEL_ORDER.indexOf(String(c || "").toLowerCase());
  return i === -1 ? 99 : i;
}

/**
 * How strangers reach the offer: a lead magnet that completely solves one narrow
 * problem, then four channel plays that give three times before they ask once.
 */
export default function LeadEngine({ engine }) {
  if (!engine) return null;
  const lm = engine.lead_magnet || {};
  const plays = [...(engine.plays || [])].sort(
    (a, b) => channelRank(a.channel) - channelRank(b.channel)
  );

  return (
    <section className="mt-20" data-testid="lead-engine-section">
      <div className="flex items-center gap-3 mb-2">
        <MagnetStraight weight="fill" className="h-5 w-5 text-[#C9A961]" />
        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
          How people find this
        </h2>
      </div>
      <p className="text-[#9AA5B5] max-w-3xl mb-8">
        An offer nobody sees is worth nothing. This is the free thing that earns
        the right to sell, and the four ways it gets in front of people.
      </p>

      {lm.name && (
        <div
          className="bg-[#0F1F38] border border-[#C9A961]/30 rounded-lg p-8 mb-6"
          data-testid="lead-magnet-card"
        >
          <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-3">
            Lead magnet {lm.format ? `· ${lm.format}` : ""}
          </div>
          <div className="font-display text-2xl font-bold text-white mb-4">
            {lm.name}
          </div>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
                The one narrow problem it solves
              </div>
              <p className="text-[#F5F1E8] leading-relaxed">
                {lm.narrow_problem}
              </p>
            </div>
            <div className="col-span-12 md:col-span-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
                Why it is a complete solution, not a teaser
              </div>
              <p className="text-[#F5F1E8] leading-relaxed">
                {lm.complete_solution}
              </p>
            </div>
            {lm.reveals_next_problem && (
              <div className="col-span-12 md:col-span-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
                  The next problem it exposes
                </div>
                <p className="text-[#F5F1E8] leading-relaxed">
                  {lm.reveals_next_problem}
                </p>
              </div>
            )}
            {lm.cta_to_core_offer && (
              <div className="col-span-12 md:col-span-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
                  The hand-off into the paid offer
                </div>
                <p className="text-[#F5F1E8] leading-relaxed">
                  {lm.cta_to_core_offer}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {plays.map((p, i) => (
          <div
            key={i}
            className="col-span-12 md:col-span-6 bg-[#0F1F38] border border-white/5 rounded-lg p-8"
            data-testid={`lead-play-${i}`}
          >
            <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-4">
              <Broadcast weight="duotone" className="h-4 w-4" />
              {p.channel}
            </div>
            <p className="text-[#F5F1E8] leading-relaxed mb-5">{p.play}</p>

            {p.give_give_give && p.give_give_give.length > 0 && (
              <>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-2">
                  Give, give, give
                </div>
                <ol className="space-y-1 mb-5">
                  {p.give_give_give.map((g, gi) => (
                    <li key={gi} className="text-sm text-[#9AA5B5]">
                      {gi + 1}. {g}
                    </li>
                  ))}
                </ol>
              </>
            )}

            {p.ask && (
              <div className="pt-4 border-t border-white/5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
                  Then ask
                </div>
                <p className="text-[#F5F1E8]">{p.ask}</p>
              </div>
            )}
            {p.weekly_volume && (
              <div className="mt-3 text-xs text-[#808A9B]">
                Cadence: {p.weekly_volume}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
